import {
    Shield24Regular,
    Gavel24Regular,
    Warning24Regular,
    LockClosed24Regular,
    PersonProhibited24Regular,
    DocumentText24Regular,
    Scales24Regular,
    Mail24Regular,
    Phone24Regular
} from "@fluentui/react-icons";
import styles from "./TermsOfUse.module.css";

const TermsAndConditions = () => {
    return (
        <main className={styles.pages}>
            <h1>Base Buddy Terms of Use</h1>
            <p className={styles.subtitle}>Effective Date: May 20, 2026</p>

            <p>
                Welcome to Base Buddy — your AI assistant for life on base. Base Buddy is an AI-driven chatbot enabled by Microsoft Azure OpenAI
                Service.
            </p>
            <p>
                These Terms of Use ("Terms") govern your use of Base Buddy (the "Service") operated by Strategi Consulting, LLC. Please read these
                Terms carefully before using the Service.
            </p>
            <p>By accessing or using the Service, you agree to be bound by these Terms.</p>
            <p>We reserve the right to update, modify, or discontinue the Service and communicate how users will be notified of such changes.</p>
            <p>
                If you reside in the European Economic Area, Switzerland, or the UK, you may not access this Service. If you choose to access this
                Service despite this restriction, you do so at your own discretion and agree to waive all rights under GDPR.
            </p>

            {/* Access and Use */}
            <section>
                <h2>
                    <DocumentText24Regular className={styles.sectionIcon} />
                    Access and Use
                </h2>
                <ol>
                    <li>
                        The Service is provided for use in accordance with these Terms and Microsoft's{" "}
                        <a href="https://www.microsoft.com/licensing/terms/product/ForOnlineServices/all#:~:text=all%20other%20rights.-,Acceptable%20Use%20Policy,-Neither%20Customer%2C%20nor">
                            Acceptable Use Policy
                        </a>{" "}
                        for Online Services and the{" "}
                        <a href="https://learn.microsoft.com/en-us/legal/cognitive-services/openai/code-of-conduct">Azure OpenAI Code of Conduct</a>{" "}
                        (hereafter incorporated as part of these "Terms").
                    </li>
                    <li>
                        <strong>Minimum Age.</strong> You must be at least 13 years old or the minimum age required in your country to consent to use
                        the Services. If you are under 18 you must have your parent or legal guardian's permission to use the Services.
                    </li>
                    <li>Excessive use of the Service may result in temporary throttling of a user's access.</li>
                    <li>We reserve the right to restrict access to any user, for any reason, at any time.</li>
                </ol>
            </section>

            {/* Accuracy of Information */}
            <section>
                <h2>
                    <Shield24Regular className={styles.sectionIcon} />
                    Accuracy of Information
                </h2>
                <ol>
                    <li>
                        The Service provides information curated directly from official government websites. While efforts are made to ensure accuracy,
                        information may not always be up-to-date or complete.
                    </li>
                    <li>
                        Users are encouraged to verify critical information with official sources and contact the appropriate individual or individuals
                        responsible for maintaining the accuracy of the information housed on the website.
                    </li>
                    <li>
                        The person or organization originating any information or content, whether publicly posted or privately transmitted through the
                        Service, bears sole responsibility. Access to such information and content is at your own risk, and we disclaim liability for any
                        errors, omissions, damages, or loss you may experience in connection with it. We lack control and are not obligated to intervene
                        in your interpretation, use, or actions resulting from exposure to the content. By using the Service, you release us from any
                        liability related to your acquisition or non-acquisition of content through it.
                    </li>
                </ol>
            </section>

            {/* Informational Use Disclaimer */}
            <section>
                <h2>
                    <Warning24Regular className={styles.sectionIcon} />
                    Informational Use Disclaimer
                </h2>
                <ol>
                    <li>
                        The Service is for educational and informational purposes only and should not be construed as professional advice. It does not
                        provide legal, medical, financial, or any other professional advice.
                    </li>
                    <li>
                        Nothing produced by the Service is a substitute for the competent advice of a licensed professional. Seek professional advice
                        before relying on information contained in this website.
                    </li>
                </ol>
            </section>

            {/* Security and Confidentiality */}
            <section>
                <h2>
                    <LockClosed24Regular className={styles.sectionIcon} />
                    Security and Confidentiality
                </h2>
                <ol>
                    <li>
                        This Service relies on public, open-source information. Do not input any personally identifiable information, sensitive,
                        controlled unclassified, or classified information into the Service.
                    </li>
                    <li>
                        We implement security measures designed to protect your information from unauthorized access. For more information on what is
                        collected and how we use and secure your information please read the current Base Buddy{" "}
                        <a href="#/privacy_policy">Privacy Policy</a>.
                    </li>
                    <li>
                        As part of providing the Azure OpenAI service, Microsoft will temporarily store Inputs and Output Content, solely for debugging
                        and to monitor for and prevent abusive or harmful uses or outputs of the service. Authorized Microsoft employees may review such
                        data that has triggered its automated systems to investigate and verify potential abuse.
                    </li>
                    <li>
                        We will not make available your prompts (inputs) and completions (outputs) to Microsoft, OpenAI, or any other third party
                        outside of the United States Department of Defense.
                    </li>
                    <li>
                        Strategi Consulting, LLC will not make available any information beyond its obligations under its agreements with its customers
                        or Microsoft.
                    </li>
                    <li>
                        Strategi Consulting, LLC will collect and store only a limited amount of data for the purpose of providing analytics to its
                        customer. For more information on what information may be collected, see our Privacy Policy.
                    </li>
                </ol>
            </section>

            {/* Prohibited Conduct */}
            <section>
                <h2>
                    <PersonProhibited24Regular className={styles.sectionIcon} />
                    Prohibited Conduct
                </h2>
                <ol>
                    <li>Users must not use the Service for any unlawful or malicious purpose.</li>
                    <li>Do not attempt to disrupt, damage, or gain unauthorized access to the Service or associated systems.</li>
                    <li>
                        Users may not use the Service to discover any underlying components of the models, algorithms, and systems, such as
                        exfiltrating the weights of models of the Service or its associated systems and components.
                    </li>
                    <li>
                        We will not use{" "}
                        <a href="https://www.microsoft.com/licensing/terms/product/Glossary/all#:~:text=Clustered%20HPC%20Applications.-,Input,-means%20all%20Customer">
                            Input
                        </a>{" "}
                        or{" "}
                        <a href="https://www.microsoft.com/licensing/terms/product/Glossary/all#:~:text=more%20Virtual%20OSEs.-,Output%20Content,-means%20any%20data">
                            Output Content
                        </a>{" "}
                        to train, retrain, or improve any third party foundation models. Users of the Service may not use Output content to create,
                        train, or improve (directly or indirectly) a similar or competing product or service.
                    </li>
                </ol>
            </section>

            {/* Warning Regarding Content */}
            <section>
                <h2>
                    <Warning24Regular className={styles.sectionIcon} />
                    Warning Regarding Content
                </h2>
                <p>
                    The Service will produce content based on the prompts you supply. The specific content generated by the Service in response to your
                    prompts cannot be anticipated in advance. The Service may generate content that you find offensive or objectionable. By agreeing to
                    these Terms and utilizing the Service, you acknowledge and assume the risk that the prompts you provide may lead to the generation of
                    content that you consider offensive or objectionable.
                </p>
            </section>

            {/* Intellectual Property */}
            <section>
                <h2>Intellectual Property</h2>
                <ol>
                    <li>Users may not reproduce, modify, or distribute the Service's content without proper authorization.</li>
                    <li>The Base Buddy name and logos are trademarks and service marks of Strategi Consulting, LLC.</li>
                    <li>Strategi Consulting, LLC does not own the Output content of the Service.</li>
                </ol>
            </section>

            {/* Limitation of Liability */}
            <section>
                <h2>
                    <Gavel24Regular className={styles.sectionIcon} />
                    Limitation of Liability
                </h2>
                <ol>
                    <li>
                        Neither Strategi Consulting, LLC nor Base Buddy and its affiliates are responsible for any direct, indirect, or consequential
                        damages arising from the use of the Service.
                    </li>
                    <li>The Service is provided "as-is" without any warranty of any kind, either expressed or implied.</li>
                </ol>
            </section>

            {/* Third-Party Liability */}
            <section>
                <h2>Third-Party Liability for Violation of Terms</h2>
                <ol>
                    <li>
                        Users accessing or using the Service shall be held responsible and liable for any damages, losses, or liabilities incurred by
                        Strategi Consulting, LLC as a result of their violation of the Terms and conditions set forth.
                    </li>
                    <li>
                        In the event that users or a third party breaches or violates any provision of these Terms or any applicable laws, regulations,
                        or rights of third parties while using the service, including but not limited to unauthorized access, misuse, or distribution of
                        content, the said user or third party shall indemnify and hold harmless Strategi Consulting, LLC, its affiliates, officers,
                        directors, employees, agents, and licensors from and against any and all claims, damages, losses, liabilities, costs, and
                        expenses, including legal fees and expenses arising out of or in connection with such breach or violation.
                    </li>
                    <li>
                        Strategi Consulting, LLC reserves the right to take appropriate legal action against any user or third party who violates these
                        Terms or infringes upon the rights of Strategi Consulting, LLC or any other party.
                    </li>
                </ol>
            </section>

            {/* Changes, Modification, Termination */}
            <section>
                <h2>Changes to Service</h2>
                <p>
                    We reserve the right to suspend or terminate specific aspects of the Service, introduce new features, impose limitations on certain
                    features, or restrict access to parts or the entirety of the Service.
                </p>
            </section>

            <section>
                <h2>Modification of Terms</h2>
                <p>
                    Strategi Consulting, LLC reserves the right to modify these Terms at any time. Continued use of the Service constitutes acceptance of
                    the modified terms.
                </p>
            </section>

            <section>
                <h2>Termination of Access</h2>
                <p>Strategi Consulting, LLC reserves the right to terminate access to the Service for users who violate these Terms.</p>
            </section>

            {/* Governing Law */}
            <section>
                <h2>
                    <Scales24Regular className={styles.sectionIcon} />
                    Governing Law
                </h2>
                <ol>
                    <li>
                        These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, United States, without
                        regard to its conflict of law principles.
                    </li>
                    <li>
                        Any legal action, suit, or proceeding arising out of or relating to these Terms or the use of the Service shall be instituted
                        in the federal or state courts located within the State of Maryland. You expressly consent and submit to the personal
                        jurisdiction of such courts for the purposes of any such legal action, suit, or proceeding.
                    </li>
                    <li>
                        By accessing or using the Service, you agree that the laws of the State of Delaware will govern the interpretation and
                        enforcement of these Terms, with the understanding that the Service is intended for use primarily across the United States.
                    </li>
                    <li>
                        If any provision of these Terms is found to be invalid by any court having competent jurisdiction, the invalidity of such
                        provision shall not affect the validity of the remaining provisions of these Terms, which shall remain in full force and effect.
                    </li>
                </ol>
            </section>

            {/* SMS Service */}
            <section>
                <h2>
                    <Phone24Regular className={styles.sectionIcon} />
                    SMS Service
                </h2>
                <p>
                    Base Buddy is also available via SMS text messaging at participating installations. Use of the SMS service is governed
                    by separate terms. See the <a href="#/sms_terms">SMS Terms & Consent</a> page for details on message frequency, data
                    rates, and how to opt in or out.
                </p>
            </section>

            {/* Contact */}
            <section>
                <h2>
                    <Mail24Regular className={styles.sectionIcon} />
                    Contact Information
                </h2>
                <p>
                    For questions or concerns regarding these Terms of Use, please contact{" "}
                    <a href="mailto:hello@basebuddy.ai">hello@basebuddy.ai</a>.
                </p>
            </section>
        </main>
    );
};

export default TermsAndConditions;
